import './App.css';
import React, { Suspense, useEffect, useState } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import Aos from 'aos';
import Toast from './components/Notifications/Toast';
import BigLoader from './components/Notifications/BigLoader';
import { ProtectedRouter } from './ProtectedRouter';
import { useDispatch, useSelector } from 'react-redux';
import SplashScreen from './screens/Splash/SplashScreen';
const Groups = React.lazy(() => import('./screens/groups/GroupsPage'));
const GroupPermissions = React.lazy(() =>
  import('./screens/groups/GroupPermissions')
);
const Permissions = React.lazy(() => import('./screens/permissions/Permissions'));
const AssignPermissions = React.lazy(() =>
  import('./screens/groups/UnAssignedPermissions')
);
const AssignUserPermissions = React.lazy(() =>
  import('./screens/users/UserUnAssignedPermissions')
);
const Users = React.lazy(() => import('./screens/users/UserPage'));
const AddUser = React.lazy(() => import('./screens/users/AddUserPage'));
const ViewUserDetails = React.lazy(() => import('./screens/users/ViewUserDetails'));
const UpdateUser = React.lazy(() => import('./screens/users/UpdateUser'));
const UserPermissions = React.lazy(() =>
  import('./screens/users/UserPermissions')
);

const Accounts = React.lazy(() => import('./screens/accounts/Accounts'));
const AuditTrails = React.lazy(() => import('./screens/audits/Audits'));
const SMSCredentials = React.lazy(() => import('./screens/e-solutions/ESolutionsSmsCredentials'));
const EmailPackages = React.lazy(() => import('./screens/email-packages/EmailPackages'));
const SMSPackages = React.lazy(() => import('./screens/sms-packages/SMSPackages'));

const Dashboard = React.lazy(() => import('./screens/Dashboard'));
const Profile = React.lazy(() => import('./screens/Settings'));
const NotFound = React.lazy(() => import('./screens/NotFound'));
const Login = React.lazy(() => import('./screens/Login'));
const Verification = React.lazy(() => import('./screens/Verification'));
const ResendVerification = React.lazy(() => import('./screens/ResendVerification'));
const RegistrationSuccess = React.lazy(() => import('./screens/RegistrationSuccess'));
const Register = React.lazy(() => import('./screens/Register'));

const Transactions = React.lazy(() => import('./screens/transactions/Transactions'));


const Currencies = React.lazy(() => import('./screens/system-parameters/currency/Currency'));
const PaynowCredentials = React.lazy(() => import('./screens/system-parameters/paynow-credentials/PesepayCredentials'));

function App() {
  const [isSplashVisible, setSplashVisible] = useState(true);
  const userInfo = useSelector((state) => state.userLogin.userInfo);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Initialize AOS
  useEffect(() => {
    Aos.init();
  }, []);



  useEffect(() => {
    if (location.pathname !== '/verification') {
      const splashShown = sessionStorage.getItem('splashShown');
      if (!splashShown) {
        const timer = setTimeout(() => {
          setSplashVisible(false);
          sessionStorage.setItem('splashShown', 'true');
        }, 3000);
        return () => clearTimeout(timer);
      } else {
        setSplashVisible(false);
      }
    } else {
      setSplashVisible(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = window.location.pathname;

    if (!userInfo || !userInfo?.jwtToken) {
      if (
        currentPath !== '/register' &&
        currentPath !== '/verification' &&
        currentPath !== '/register-success' &&
        currentPath !== '/resend-verification'
      ) {
        navigate('/login');
      }
    }
  }, [navigate, userInfo, location.pathname]);


  useEffect(() => {
    Aos.init();
    const timer = setTimeout(() => setSplashVisible(false), 3000);
    return () => clearTimeout(timer);
  }, []);


  if (isSplashVisible && (location.pathname !== '/verification' || location.pathname !== '/register-success' || location.pathname !== '/resend-verification')) {
    return <SplashScreen />;
  }



  return (
    <>
      {/* Toaster */}
      <Toast />
      <Routes>
        <Route
          path="/login"
          element={
            <Suspense fallback={<BigLoader />}>
              <Login />
            </Suspense>
          }
        />
        <Route
          path="/register"
          element={
            <Suspense fallback={<BigLoader />}>
              <Register />
            </Suspense>
          }
        />

        <Route
          path="/verification"
          element={
            <Suspense fallback={<BigLoader />}>
              <Verification />
            </Suspense>
          }
        />

        <Route
          path="/resend-verification"
          element={
            <Suspense fallback={<BigLoader />}>
              <ResendVerification />
            </Suspense>
          }
        />

        <Route
          path="/register-success"
          element={
            <Suspense fallback={<BigLoader />}>
              <RegistrationSuccess />
            </Suspense>
          }
        />


        {/* PROTECTED */}
        <Route element={<ProtectedRouter />}>
          <Route
            path="/"
            element={
              <Suspense fallback={<BigLoader />}>
                <Dashboard />
              </Suspense>
            }
          />
          <Route
            path="/users/all"
            element={
              <Suspense fallback={<BigLoader />}>
                <Users />
              </Suspense>
            }
          />
          <Route
            path="/user-management/user/add"
            element={
              <Suspense fallback={<BigLoader />}>
                <AddUser />
              </Suspense>
            }
          />
          <Route
            path="/user-management/users/view/:userId"
            element={
              <Suspense fallback={<BigLoader />}>
                <ViewUserDetails />
              </Suspense>
            }
          />
          <Route
            path="/user-management/users/edit/:userId"
            element={
              <Suspense fallback={<BigLoader />}>
                <UpdateUser />
              </Suspense>
            }
          />
          <Route
            path="/admin-control/roles"
            element={
              <Suspense fallback={<BigLoader />}>
                <Groups />
              </Suspense>
            }
          />
          <Route
            path="/admin-control/permissions"
            element={
              <Suspense fallback={<BigLoader />}>
                <Permissions />
              </Suspense>
            }
          />
          <Route
            path="/admin-control/permissions/:id"
            element={
              <Suspense fallback={<BigLoader />}>
                <GroupPermissions />
              </Suspense>
            }
          />

          <Route
            path="/user-management/groups/unassigned-permissions/:id"
            element={
              <Suspense fallback={<BigLoader />}>
                <AssignPermissions />
              </Suspense>
            }
          />
          <Route
            path="/user-management/user/unassigned-permissions/:id"
            element={
              <Suspense fallback={<BigLoader />}>
                <AssignUserPermissions />
              </Suspense>
            }
          />
          <Route
            path="/users/:id/permissions"
            element={
              <Suspense fallback={<BigLoader />}>
                <UserPermissions />
              </Suspense>
            }
          />
         
          <Route
            path="/profile"
            element={
              <Suspense fallback={<BigLoader />}>
                <Profile />
              </Suspense>
            }
          />
          <Route
            path="/transactions"
            element={
              <Suspense fallback={<BigLoader />}>
                <Transactions />
              </Suspense>
            }
          />
         
          <Route
            path="/parameters/currencies"
            element={
              <Suspense fallback={<BigLoader />}>
                <Currencies />
              </Suspense>
            }
          />

          <Route
            path="/parameters/pesepay-credentials"
            element={
              <Suspense fallback={<BigLoader />}>
                <PaynowCredentials />
              </Suspense>
            }
          />

         
          {/* DATA */}

          <Route
            path="/accounts"
            element={
              <Suspense fallback={<BigLoader />}>
                <Accounts />
              </Suspense>
            }
          />

          <Route
            path="/audit-trail"
            element={
              <Suspense fallback={<BigLoader />}>
                <AuditTrails />
              </Suspense>
            }
          />

          <Route
            path="/sms-credentials/e-solutions"
            element={
              <Suspense fallback={<BigLoader />}>
                <SMSCredentials />
              </Suspense>
            }
          />

          <Route
            path="/parameters/email-packages"
            element={
              <Suspense fallback={<BigLoader />}>
                <EmailPackages />
              </Suspense>
            }
          />

          <Route
            path="/parameters/sms-packages"
            element={
              <Suspense fallback={<BigLoader />}>
                <SMSPackages />
              </Suspense>
            }
          />


        </Route>
        <Route
          path="*"
          element={
            <Suspense fallback={<BigLoader />}>
              <NotFound />
            </Suspense>
          }
        />
      </Routes>
    </>
  );
}

export default App;
